import {
  Checkbox,
  getFontCss,
  HoverableLinkDesigns,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
  TripleCheckboxStates,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { TransactionDownloadFieldValue, TransactionsDownloadFields } from 'src/component-modal/DownloadTransactionsModal/DownloadTransactionsModal'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import Trns from 'src/localization/Trns'
import { css } from 'styled-components'

interface CustomizeCsvDownloadOptionsProps {
  flatFields: TransactionsDownloadFields
  onFlatFieldChange: (
    key: keyof TransactionsDownloadFields,
    value: TransactionDownloadFieldValue,
  ) => void
  onMultiFlatFieldChange: (
    fieldNames: Array<keyof TransactionsDownloadFields>,
    values: TransactionDownloadFieldValue[],
  ) => void
  includeAttachments: boolean
  setIncludeAttachments: (val: boolean) => void
  transactionFieldSelectionError: boolean
}

const CustomizeCsvDownloadOptions: React.FC<
  CustomizeCsvDownloadOptionsProps
> = (props) => {
  const [selectAll, setSelectAll] = useState<boolean | TripleCheckboxStates>(
    false,
  )

  useEffect((): void => {
    if (
      Object.entries(props.flatFields).some(([key, value]) => value.value) &&
      Object.entries(props.flatFields).some(([key, value]) => !value.value)
    ) {
      setSelectAll(TripleCheckboxStates.Partial)
    }
    if (Object.entries(props.flatFields).every(([key, value]) => value.value)) {
      setSelectAll(true)
    }
    if (
      Object.entries(props.flatFields).every(([key, value]) => !value.value)
    ) {
      setSelectAll(false)
    }
  }, [props.flatFields])

  const handleFieldSelection = (
    key: string,
    value: boolean,
    name: string,
  ): void => {
    props.onFlatFieldChange(key as keyof TransactionsDownloadFields, {
      fieldName: name,
      value: !value,
    })
  }

  const handleIncludeAttachmentsClick = (): void => {
    props.setIncludeAttachments(!props.includeAttachments)
  }

  const column = (
    fieldValue: TransactionDownloadFieldValue,
    cookieKey: string,
  ): React.ReactElement => {
    return (
      <div
        css={css`
          display: flex;
        `}
        className="column"
      >
        <Checkbox
          onClick={(): void => {
            handleFieldSelection(
              cookieKey,
              fieldValue.value,
              fieldValue.fieldName,
            )
          }}
          isSelected={fieldValue.value}
          css={css`
            align-self: flex-start;
          `}
        />
        <HoverableLink
          onClick={(): void => {
            handleFieldSelection(
              cookieKey,
              fieldValue.value,
              fieldValue.fieldName,
            )
          }}
          ariaLabelSource={fieldValue.fieldName}
          regular
          design={HoverableLinkDesigns.Primary}
        >
          <Trns source={fieldValue.fieldName} />
        </HoverableLink>
      </div>
    )
  }

  const handleSelectAllClick = (): void => {
    setSelectAll(!selectAll)
    props.onMultiFlatFieldChange(
      Object.keys(props.flatFields) as Array<keyof TransactionsDownloadFields>,
      Object.values(props.flatFields).map((fieldValue) => ({
        fieldName: fieldValue.fieldName,
        value: !selectAll,
      })),
    )
  }

  return (
    <div
      css={css`
        margin-top: 24px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )}
          `}
        >
          <Trns source={'Customize CSV'} />
        </span>
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <Checkbox
            onClick={handleSelectAllClick}
            isSelected={selectAll}
            css={css`
              align-self: flex-start;
              margin-right: 0px;
            `}
          />
          <HoverableLink
            onClick={handleSelectAllClick}
            ariaLabelSource={'Select All'}
            regular
            size={HoverableLinkSizes.Small}
            design={HoverableLinkDesigns.Primary}
          >
            <Trns source={'Select All'} />
          </HoverableLink>
        </div>
      </div>
      <div
        css={css`
          margin-top: 12px;
          max-height: 200px;
          overflow: scroll;
          border-radius: 10px;
          border: 1px solid ${styleConstants.colors.gray_medium};
          background-color: ${styleConstants.colors.white};
          padding: 20px;

          .column:nth-child(n + 2) {
            margin-top: 12px;
          }
        `}
      >
        {Object.entries(props.flatFields).map(([key, value]) => {
          return column(value, key)
        })}
      </div>
      {props.transactionFieldSelectionError && (
        <div
          css={css`
            ${getFontCss(
              14,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )};
            margin-top: 12px;
          `}
        >
          <Trns source={'Please select at least one field'} />
        </div>
      )}
      <div
        css={css`
          display: flex;
          margin-top: 40px;
        `}
      >
        <Checkbox
          onClick={handleIncludeAttachmentsClick}
          isSelected={props.includeAttachments}
          css={css`
            align-self: flex-start;
          `}
        />
        {/* figure out why hoverable link screws up all the styling on the element */}
        <div
          onClick={handleIncludeAttachmentsClick}
          onKeyPress={handleIncludeAttachmentsClick}
          role="button"
          tabIndex={0}
          css={css`
            ${getFontCss(
              14,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            &:hover {
              ${getFontCss(
                14,
                themeConstants.colors.cta,
                styleConstants.fontFamilies.regular,
              )};
            }
          `}
        >
          <Trns
            source={
              'Include attachments associated with the transactions you wish to download (200 attachments max).'
            }
          />
        </div>
      </div>
    </div>
  )
}

export default CustomizeCsvDownloadOptions
